/**
 * Immutable Audit Trail, Cryptographic Timestamping & Kernel Integrity Module (`web/src/lib/layoutEnhancers.ts`).
 *
 * @statutory OR Art. 957a Abs. 3 & Art. 958f · GeBüV Art. 3–10 (Integrität, Prüfspur & unveränderbare Aufbewahrung) · ZertES (SR 943.03 / RFC 3161 Zeitstempel) · OR Art. 728a (IKS) · PS-CH 230 / 890
 */
/**
 * Shared layout enhancers — one observer for the whole app instead of per-page fixes (taste pass 02, R3–R5).
 *
 * 1. Card-mode tables (≤ 900 px, app.css): every `table.data` cell gets `data-label` = its column caption, so the
 *    stacked card still says what "1’234.50" is (Soll, Haben, Saldo …). Rendered by `td[data-label]::before`.
 *    Works for all ~270 tables without touching their JSX; an explicit `data-label` on a cell is never overwritten.
 * 2. Tab bars (`.tabs`, `[role=tablist]`): `data-overflow="start|end|both"` drives an edge fade so a clipped last
 *    label reads as "scroll for more"; the selected tab is scrolled into view. Horizontally scrolling `.table-wrap`
 *    regions get tabindex=0 and a label so keyboard users can scroll them (axe scrollable-region-focusable), but NO
 *    fade: a mask there washed out the rightmost visible amounts (Grok L13 review). Do not add it back.
 * 3. Single-line text that is cut (combobox value, labelled native select) gets a `title` with the full text.
 *    Badges are not cut at all: they wrap at spaces and never hyphenate (defensive.css).
 */

const LABELED = 'data-kontor-label';

function headerLabels(table: HTMLTableElement): string[] {
  const row = table.tHead?.rows[table.tHead.rows.length - 1];
  if (!row) return [];
  const labels: string[] = [];
  for (const th of Array.from(row.cells)) {
    // a spanning <th> is a group caption ("Einkommen" over code + label + amount), not a column name
    const text = th.colSpan > 1 ? '' : (th.getAttribute('aria-label') ?? th.textContent ?? '').replace(/\s+/g, ' ').trim();
    for (let i = 0; i < Math.max(1, th.colSpan); i++) labels.push(text);
  }
  return labels;
}

export function labelTable(table: HTMLTableElement) {
  const labels = headerLabels(table);
  if (!labels.some(Boolean)) return;
  const bodies = [...Array.from(table.tBodies), ...(table.tFoot ? [table.tFoot] : [])];
  for (const body of bodies) {
    for (const tr of Array.from(body.rows)) {
      let col = 0;
      for (const td of Array.from(tr.cells)) {
        const label = td.colSpan > 1 ? '' : labels[col] ?? '';
        col += Math.max(1, td.colSpan);
        // explicit labels from the page win; our own earlier label is refreshed (columns can change)
        if (td.hasAttribute('data-label') && !td.hasAttribute(LABELED)) continue;
        // write only on change: the journal has ~2400 cells and this runs after every DOM mutation
        if (label) { if (td.getAttribute('data-label') !== label) { td.setAttribute('data-label', label); td.setAttribute(LABELED, ''); } }
        else if (td.hasAttribute(LABELED)) { td.removeAttribute('data-label'); td.removeAttribute(LABELED); }
      }
    }
  }
}

export function markOverflow(el: HTMLElement) {
  const max = el.scrollWidth - el.clientWidth;
  const start = el.scrollLeft > 1;
  const end = max - el.scrollLeft > 1;
  const v = start && end ? 'both' : start ? 'start' : end ? 'end' : '';
  if (v) { if (el.getAttribute('data-overflow') !== v) el.setAttribute('data-overflow', v); }
  else if (el.hasAttribute('data-overflow')) el.removeAttribute('data-overflow');
}

/** Only boxes that really scroll get a fade: at ≤ 900 px app.css sets `.table-wrap { overflow-x: visible }`, and a
 *  mask there would hide the overflowing content instead of hinting at it. */
function scrollsX(el: HTMLElement) {
  if (typeof getComputedStyle !== 'function') return true;
  const o = getComputedStyle(el).overflowX;
  return (o === 'auto' || o === 'scroll') && el.scrollWidth > el.clientWidth + 1;
}

// last active tab seen per list: re-centre only when the selection changes, never while the user scrolls the bar
const lastActive = new WeakMap<HTMLElement, HTMLElement | null>();
const watchedTabs = new WeakSet<HTMLElement>();
export function enhanceTabs(list: HTMLElement) {
  if (!watchedTabs.has(list)) { watchedTabs.add(list); list.addEventListener('scroll', () => markOverflow(list), { passive: true }); }
  const active = list.querySelector<HTMLElement>('[aria-selected="true"], [aria-pressed="true"]');
  if (!scrollsX(list)) { if (list.hasAttribute('data-overflow')) list.removeAttribute('data-overflow'); return; }
  // remembered only once the bar scrolls, so a list that starts hidden or short still centres its tab later
  const changed = lastActive.get(list) !== active;
  lastActive.set(list, active);
  if (active && changed) {
    const a = active.getBoundingClientRect(), l = list.getBoundingClientRect();
    if (a.left < l.left || a.right > l.right) list.scrollLeft += a.left - l.left - (l.width - a.width) / 2;
  }
  markOverflow(list);
}

function regionLabel(wrap: HTMLElement) {
  const en = typeof document !== 'undefined' && document.documentElement.lang === 'en';
  const heading = wrap.closest('.card, section')?.querySelector('h2, h3, h4')?.textContent?.replace(/\s+/g, ' ').trim();
  const base = en ? 'Table, scrolls horizontally' : 'Tabelle, horizontal scrollbar';
  return heading ? `${heading} – ${base}` : base; // unique per card where the card has a heading (axe landmark-unique)
}

const watchedWraps = new WeakSet<HTMLElement>();
export function enhanceTableWrap(wrap: HTMLElement) {
  if (!watchedWraps.has(wrap)) {
    watchedWraps.add(wrap);
    wrap.addEventListener('scroll', () => markOverflow(wrap), { passive: true });
  }
  const scrolls = scrollsX(wrap);
  if (scrolls) markOverflow(wrap); else if (wrap.hasAttribute('data-overflow')) wrap.removeAttribute('data-overflow');
  const ours = wrap.hasAttribute('data-kontor-scroll');
  if (scrolls && !ours && !wrap.hasAttribute('tabindex')) {
    wrap.tabIndex = 0; wrap.setAttribute('data-kontor-scroll', '');
    if (!wrap.hasAttribute('aria-label') && !wrap.hasAttribute('aria-labelledby')) {
      wrap.setAttribute('role', 'region'); wrap.setAttribute('aria-label', regionLabel(wrap)); wrap.setAttribute('data-kontor-region', '');
    }
  } else if (!scrolls && ours && document.activeElement !== wrap) { // never drop focus to <body> mid-interaction
    wrap.removeAttribute('tabindex'); wrap.removeAttribute('data-kontor-scroll');
    if (wrap.hasAttribute('data-kontor-region')) { wrap.removeAttribute('aria-label'); wrap.removeAttribute('role'); wrap.removeAttribute('data-kontor-region'); }
  }
}

let ruler: CanvasRenderingContext2D | null = null;
/** A <select> paints its text internally (scrollWidth == clientWidth), so measure the selected text instead. */
function selectIsCut(sel: HTMLSelectElement, text: string) {
  ruler ??= document.createElement('canvas').getContext('2d');
  if (!ruler || !text) return false;
  const cs = getComputedStyle(sel);
  ruler.font = cs.font;
  const room = sel.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
  return ruler.measureText(text).width > room + 1;
}

function titleIfCut(el: HTMLElement, full: string, cut = el.scrollWidth > el.clientWidth + 1) {
  if (cut && full) { if (el.title !== full) el.title = full; }
  else if (el.hasAttribute('data-kontor-title')) el.removeAttribute('title');
  if (cut) { if (!el.hasAttribute('data-kontor-title')) el.setAttribute('data-kontor-title', ''); }
  else if (el.hasAttribute('data-kontor-title')) el.removeAttribute('data-kontor-title');
}

function enhanceTitles(root: ParentNode) {
  for (const el of Array.from(root.querySelectorAll<HTMLElement>('.combo-trigger .val, .combo-trigger .label'))) {
    if (el.hasAttribute('title') && !el.hasAttribute('data-kontor-title')) continue;
    titleIfCut(el, (el.textContent ?? '').trim());
  }
  for (const sel of Array.from(root.querySelectorAll<HTMLSelectElement>('select'))) {
    if (sel.hasAttribute('title') && !sel.hasAttribute('data-kontor-title')) continue;
    // a title must not become the only name of an unlabeled select (axe label-title-only); those need a real label
    if (!sel.labels?.length && !sel.hasAttribute('aria-label') && !sel.hasAttribute('aria-labelledby')) continue;
    const text = sel.selectedOptions[0]?.textContent?.trim() ?? '';
    titleIfCut(sel, text, selectIsCut(sel, text));
  }
}

export function enhanceLayout(root: ParentNode = document) {
  root.querySelectorAll<HTMLTableElement>('table.data').forEach(labelTable); // only table.data has a card mode
  root.querySelectorAll<HTMLElement>('.tabs, [role="tablist"]').forEach(enhanceTabs);
  root.querySelectorAll<HTMLElement>('.table-wrap').forEach(enhanceTableWrap);
  enhanceTitles(root);
}

export function installLayoutEnhancers() {
  let queued = false;
  const run = () => { queued = false; enhanceLayout(); };
  const schedule = () => { if (!queued) { queued = true; requestAnimationFrame(run); } };
  // characterData: a column caption or a badge text can change in place (i18n switch, live counts)
  // open popovers and listboxes (Combobox hover moves aria-selected on every mousemove) never change tables or tab bars
  const inPopover = (n: Node) => (n instanceof Element ? n : n.parentElement)?.closest('.popover, [role="listbox"]');
  new MutationObserver((records) => { if (records.some((r) => !inPopover(r.target))) schedule(); }).observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['aria-selected', 'aria-pressed'] });
  window.addEventListener('resize', schedule, { passive: true });
  document.addEventListener('change', schedule);
  schedule();
}
